import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout, { useDateRange } from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { ClipboardList, Search, Download, Loader2, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiClient } from '../../api/client';
import AdminErrorBoundary from '../../components/admin/AdminErrorBoundary';

const AdminAudit = () => {
  const { dateRange } = useDateRange();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [actionSummary, setActionSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, period: dateRange });
      if (actionFilter) params.append('action', actionFilter);
      if (userFilter) params.append('user_id', userFilter);
      const data = await apiClient.request(`/api/admin/audit-trail?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setActionSummary(data.action_summary || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, dateRange, actionFilter, userFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dateRange, actionFilter, userFilter]);

  const handleExport = async () => {
    const data = await apiClient.request(`/api/admin/export/audit?format=csv`);
    const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data, null, 2)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `hibff_audit_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  // Defensive guard: backend may occasionally emit rows with missing
  // `action` strings — coerce to empty string so the chart never throws
  // (this was the silent crash that blanked the Audit Trail page).
  const chartData = (actionSummary || []).slice(0, 10).map(a => ({
    name: (a && typeof a.action === 'string' ? a.action : '').replace(/_/g, ' ').slice(0, 20),
    count: (a && typeof a.count === 'number') ? a.count : 0,
  }));

  return (
    <AdminLayout pageTitle="Audit Trail">
      <AdminErrorBoundary pageName="Audit Trail">
      <CheatSheet pageId="audit" />
      {/* Top Chart */}
      {chartData.length > 0 && (
        <div className="bg-white p-5 rounded-lg mb-6" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Event Distribution ({dateRange})</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
              <Tooltip /><Bar dataKey="count" fill="#F5831F" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={actionFilter} onChange={e => setActionFilter(e.target.value)} placeholder="Filter by action (e.g. login, guardrail)..."
            className="pl-9 pr-4 py-2 border rounded-lg text-sm w-full" style={{ borderColor: '#E0E0E0' }} />
        </div>
        <div className="relative max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={userFilter} onChange={e => setUserFilter(e.target.value)} placeholder="Filter by user ID..."
            className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64" style={{ borderColor: '#E0E0E0' }} />
        </div>
        <button onClick={handleExport} className="px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 text-white ml-auto" style={{ backgroundColor: '#F5831F' }}>
          <Download size={14} /> Export
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
        <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: '#E0E0E0' }}>
          <span className="text-sm font-medium" style={{ color: '#333333' }}>{total} events</span>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
            {['Timestamp', 'Action', 'User ID', 'Details'].map(h => <th key={h} className="text-left p-3 text-xs font-medium whitespace-nowrap" style={{ color: '#44403A' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-sm" style={{ color: '#44403A' }}>No audit events found</td></tr>
            ) : logs.map((log, i) => (
              <tr key={i} className="border-t" style={{ borderColor: '#E0E0E0' }}>
                <td className="p-3 text-xs font-mono" style={{ color: '#44403A' }}>{log.timestamp?.slice(0, 19)}</td>
                <td className="p-3"><span className="px-2 py-0.5 text-xs rounded-full font-medium" style={{
                  backgroundColor: log.action?.includes('guardrail') ? '#DC262615' : log.action?.includes('login') ? '#2EAA5C15' : log.action?.includes('admin') ? '#F5831F15' : '#44403A15',
                  color: log.action?.includes('guardrail') ? '#DC2626' : log.action?.includes('login') ? '#2EAA5C' : log.action?.includes('admin') ? '#F5831F' : '#44403A',
                }}>{log.action}</span></td>
                <td className="p-3 text-xs font-mono" style={{ color: '#44403A' }}>{log.user_id?.slice(0, 12)}...</td>
                <td className="p-3 text-xs" style={{ color: '#44403A' }}>{log.metadata ? JSON.stringify(log.metadata).slice(0, 80) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {pages > 1 && (
          <div className="p-3 flex items-center justify-center gap-3 border-t" style={{ borderColor: '#E0E0E0' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-1.5 rounded disabled:opacity-30"><ChevronLeft size={16} /></button>
            <span className="text-sm" style={{ color: '#44403A' }}>Page {page} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className="p-1.5 rounded disabled:opacity-30"><ChevronRight size={16} /></button>
          </div>
        )}
      </div>
      </AdminErrorBoundary>
    </AdminLayout>
  );
};

export default AdminAudit;
